import React from "react";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";

interface HistoryFilters {
  page: number;
  limit: number;
  assetType?: "pump" | "valve" | "motor";
  serviceType?: "Preventive" | "Corrective" | "Inspection";
  failureMode?: "bearing" | "seal" | "electrical" | "none";
  dateFrom?: string;
  dateTo?: string;
  search_data?: string;
}

interface HistoryFilterPanelProps {
  currentFilters: Partial<HistoryFilters>;
  onApplyFilters: (filters: Partial<HistoryFilters>) => void;
  onResetFilters: () => void;
}

export const HistoryFilterPanel: React.FC<HistoryFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Define form fields configuration
  const filterFields: FormField[] = [
    {
      name: "assetType",
      type: "select",
      label: "Asset Type",
      placeholder: "Select asset type",
      className: "col-span-1",
      defaultValue: currentFilters.assetType || "all",
      options: [
        { value: "all", label: "All Asset Types" },
        { value: "pump", label: "Pump" },
        { value: "valve", label: "Valve" },
        { value: "motor", label: "Motor" },
      ],
      fullWidth: true,
    },
    {
      name: "failureMode",
      type: "select",
      label: "Failure Mode",
      placeholder: "Select failure mode",
      className: "col-span-1",
      defaultValue: currentFilters.failureMode || "all",
      options: [
        { value: "all", label: "All Failure Modes" },
        { value: "bearing", label: "Bearing Failure" },
        { value: "seal", label: "Seal Failure" },
        { value: "electrical", label: "Electrical Issue" },
        { value: "none", label: "No Failure (N/A)" },
      ],
      fullWidth: true,
    },
    {
      name: "dateFrom",
      type: "datepicker",
      label: "Date From",
      placeholder: "Select date range",
      className: "col-span-1",
      defaultValue: currentFilters.dateFrom || "",
      fullWidth: true,
    },
    {
      name: "dateTo",
      type: "datepicker",
      label: "Date To",
      placeholder: "Select date range",
      className: "col-span-1",
      defaultValue: currentFilters.dateTo || "",
      fullWidth: true,
    },
  ];

  // Create form instance with default values from current filters
  const form = FormService.createForm({
    fields: filterFields,
    defaultValues: {
      assetType: currentFilters.assetType || "all",
      serviceType: currentFilters.serviceType || "all",
      failureMode: currentFilters.failureMode || "all",
      dateFrom: currentFilters.dateFrom || "",
      dateTo: currentFilters.dateTo || "",
    },
  });

  // Handle form submission
  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<HistoryFilters> = {};

    // Only include non-"all" and non-empty values in the filters
    if (data.assetType && data.assetType !== "all") {
      filtersToApply.assetType = data.assetType;
    }

    if (data.serviceType && data.serviceType !== "all") {
      filtersToApply.serviceType = data.serviceType;
    }

    if (data.failureMode && data.failureMode !== "all") {
      filtersToApply.failureMode = data.failureMode;
    }

    if (data.dateFrom && data.dateFrom.trim()) {
      filtersToApply.dateFrom = data.dateFrom.trim();
    }

    if (data.dateTo && data.dateTo.trim()) {
      filtersToApply.dateTo = data.dateTo.trim();
    }

    onApplyFilters(filtersToApply);
  };

  // Handle form reset
  const handleResetFilters = () => {
    form.reset({
      assetType: "all",
      serviceType: "all",
      failureMode: "all",
      dateFrom: "",
      dateTo: "",
    });
    onResetFilters();
  };

  // Custom footer with Reset and Apply buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-5 flex justify-end gap-2 pt-6 border-t border-gray-200">
      <Button type="button" variant="outline" onClick={handleResetFilters}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={filterFields}
      form={form}
      onSubmit={handleApplyFilters}
      onReset={handleResetFilters}
      config={{
        layout: "horizontal",
        spacing: "medium",
        showSubmitButton: false,
        showResetButton: false,
        classes: {
          fieldContainer: "flex flex-col gap-1 mb-4",
          label: "text-sm text-gray-800 font-semibold",
          input: "rounded-md border px-3 py-2 border-gray-300 w-full",
          select: "rounded-md border px-3 py-2 border-gray-300",
          textarea: "rounded-md border px-3 py-2 border-gray-300",
          error: "text-sm text-red-500 mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      }}
      className="bg-white p-6 rounded-lg border border-gray-200"
      gridColumns={6}
      responsiveColumns={{
        sm: 1,
        md: 2,
      }}
      renderCustomFooter={renderCustomFooter}
    />
  );
};
